/**
 * Small link helpers shared by the sign-in dialog, the account pages, the claim box, the wallet
 * and the legacy AuthModal / SignupPage (they are in the main bundle, so this file stays tiny:
 * no React; lib/urlSecrets for the kept forgot-password address).
 */
import { clearUrlSecret, readUrlSecret, stashUrlSecret } from '../../../lib/urlSecrets';

/** Control characters (after decoding too: an encoded tab or newline counts). */
// eslint-disable-next-line no-control-regex
const CONTROL_RE = /[\u0000-\u001f\u007f]/;

/**
 * True when `path` could leave this origin: it starts with "//" or "/\\" (protocol-relative),
 * holds a backslash or a control character, or carries a scheme ("javascript:", "https:").
 */
const leavesOrigin = (path) =>
  !path.startsWith('/') || /^\/[/\\]/.test(path) || /\\/.test(path) || CONTROL_RE.test(path) || /^[a-z][a-z0-9+.-]*:/i.test(path);

/**
 * A same-origin, in-app path from an untrusted redirect value (a `?redirect=` parameter,
 * the `postAuthRedirect` kept in sessionStorage), else null.
 *
 * Accepted: a relative path starting with one "/" ("/deal-wallet", "/business/onboarding?venue=v1#x").
 * Refused: absolute URLs ("https://evil.example"), protocol-relative ones ("//evil.example",
 * "/\\evil.example"), other schemes ("javascript:…"), control characters and backslashes
 * anywhere, and anything that does not resolve to this origin.
 *
 * The value is checked twice: as given, and again after the URL parser has normalised it,
 * because dot segments collapse into a protocol-relative path ("/.//evil.example",
 * "/%2e//evil.example" and "/..//evil.example" all become "//evil.example"). The normalised
 * pathname is also checked decoded ("/%2f/evil.example" → "//evil.example"). The returned
 * value is the normalised pathname + search + hash, safe for `navigate()` and
 * `window.location.href` alike.
 */
export function safeRedirectPath(value) {
  if (typeof value !== 'string') return null;
  const v = value.trim();
  if (!v || leavesOrigin(v) || v.startsWith('//')) return null;
  if (typeof window === 'undefined' || !window.location) return null;
  try {
    const { origin } = window.location;
    const url = new URL(v, origin);
    if (url.origin !== origin) return null;
    const out = `${url.pathname}${url.search}${url.hash}`;
    if (leavesOrigin(out) || leavesOrigin(url.pathname)) return null;
    let decoded;
    try {
      decoded = decodeURIComponent(url.pathname);
    } catch {
      return null; // a malformed escape: not a path we link to
    }
    if (leavesOrigin(decoded)) return null;
    return out;
  } catch {
    return null;
  }
}

/** The longest address the forgot-password link carries (RFC 5321 path limit). */
const EMAIL_MAX = 254;

/** A usable address: trimmed, at most 254 characters, no whitespace; else ''. */
const usableEmail = (value) => {
  const v = typeof value === 'string' ? value.trim() : '';
  return v && v.length <= EMAIL_MAX && !/\s/.test(v) ? v : '';
};

/** The forgot-password page (where the address is kept for). */
export const FORGOT_PASSWORD_PATH = '/forgot-password';

/**
 * `?email=` of the forgot-password page (the "Set a password" links in the claim, wallet and
 * venue-approval emails). Trimmed; '' when absent, over-long or holding whitespace (a raw "+"
 * reads as a space, so such a link gives no prefill rather than a wrong one).
 */
export const emailFromSearch = (searchParams) => usableEmail(searchParams?.get('email') || '');

/**
 * The address the forgot-password page starts with: `?email=` still in the URL (an older tab,
 * a test), else the one kept for /forgot-password (the index.html URL secret guard took the
 * emailed link's `?email=` out of the address bar; the in-app handoffs below). '' when none.
 * `location`: the router's location (default window.location).
 */
export function forgotPasswordEmail(location) {
  return usableEmail(readUrlSecret('email', { location }) || '');
}

/**
 * Hand a typed address to /forgot-password without putting it in the URL or in history.state
 * (Google Tag Manager's history events copy both): it is kept in sessionStorage for that page
 * (lib/urlSecrets, the guard's format), read by forgotPasswordEmail and cleared once the reset
 * link has been sent. Call it when the visitor goes there (a click), then navigate to
 * FORGOT_PASSWORD_PATH with no state. Nothing usable: any address kept earlier is dropped.
 */
export function stashForgotPasswordEmail(email) {
  const v = usableEmail(email);
  if (v) stashUrlSecret('email', v, { pathname: FORGOT_PASSWORD_PATH });
  else clearUrlSecret('email');
}

/** The kept address is no longer needed (the reset link was sent). */
export const clearForgotPasswordEmail = () => clearUrlSecret('email');
